import { useEffect, useState } from 'react';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  Boolean(window.matchMedia) &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Plays `steps` ([value, holdMs] pairs) on a loop while the node is mostly on
// screen, starting again from `initial` each time it comes back into view.
// With reduced motion, or without IntersectionObserver, it rests on `final`.
// Keep `steps` at module scope so the effect does not restart every render.
export default function useScreenLoop(ref, { initial, steps, final, delay = 700 }) {
  const [value, setValue] = useState(initial);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') {
      setValue(final);
      return undefined;
    }

    let timer = 0;
    let running = false;
    const play = (index) => {
      const [next, hold] = steps[index];
      setValue(next);
      timer = window.setTimeout(() => play((index + 1) % steps.length), hold);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        if (entry.isIntersecting && !running) {
          running = true;
          setValue(initial);
          timer = window.setTimeout(() => play(0), delay);
        } else if (!entry.isIntersecting && running) {
          running = false;
          window.clearTimeout(timer);
        }
      },
      { threshold: 0.6 }
    );
    observer.observe(node);

    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [ref, initial, steps, final, delay]);

  return value;
}
